import { Controller } from "@hotwired/stimulus"

// Handles the loyalty-program edit form:
// - Applies presets to the sentence inputs
// - Toggles the expiry-days input with the
//   expiry-enabled switch
// - Updates the live "Program preview" card
//   whenever any input changes
//
// Sentence templates come from data values so
// the view owns translations (via GetText).
// Placeholders:
//   %{spend}, %{points}, %{discount},
//   %{percent}, %{label}, %{minimum}, %{minSpend}
export default class extends Controller {
  static targets = [
    "pointsEarned",
    "currencyUnit",
    "pointValue",
    "minimumPoints",
    "expiryToggle",
    "expiryWrapper",
    "preview",
    "previewSpend",
    "previewRedeem",
    "previewRatio",
    "previewMinimum",
  ]

  static values = {
    currency: { type: String, default: "MXN" },
    locale: { type: String, default: "en-US" },
    sampleSpend: { type: Number, default: 250 },
    spendTemplate: { type: String, default: "" },
    redeemTemplate: { type: String, default: "" },
    ratioTemplate: { type: String, default: "" },
    minimumTemplate: { type: String, default: "" },
    emptyMessage: { type: String, default: "" },
    labelGenerous: {
      type: String, default: "very generous"
    },
    labelBalanced: {
      type: String, default: "balanced"
    },
    labelConservative: {
      type: String, default: "conservative"
    },
  }

  static presets = {
    simple: {
      points: 1,
      currencyUnit: 1,
      pointValue: 1,
      minimum: 100,
    },
    balanced: {
      points: 1,
      currencyUnit: 1,
      pointValue: 0.1,
      minimum: 500,
    },
    scoreboard: {
      points: 10,
      currencyUnit: 1,
      pointValue: 0.01,
      minimum: 5000,
    },
  }

  connect() {
    this.formatter = new Intl.NumberFormat(
      this.localeValue,
      {
        style: "currency",
        currency: this.currencyValue,
        minimumFractionDigits: 2,
      }
    )
    this.integerFormatter = new Intl.NumberFormat(
      this.localeValue
    )
    this.syncExpiryVisibility()
    this.updatePreview()
  }

  applyPreset(event) {
    const key = event.params.preset
    const preset = this.constructor.presets[key]
    if (!preset) return

    this.setIntegerValue(
      this.pointsEarnedTarget,
      preset.points
    )
    this.setMaskedCurrencyValue(
      this.currencyUnitTarget,
      preset.currencyUnit
    )
    this.setMaskedCurrencyValue(
      this.pointValueTarget,
      preset.pointValue
    )
    this.setIntegerValue(
      this.minimumPointsTarget,
      preset.minimum
    )

    this.updatePreview()
  }

  toggleExpiry() {
    this.syncExpiryVisibility()
  }

  syncExpiryVisibility() {
    if (!this.hasExpiryToggleTarget) return
    if (!this.hasExpiryWrapperTarget) return

    const enabled = this.expiryToggleTarget.checked
    const el = this.expiryWrapperTarget
    el.classList.toggle("hidden", !enabled)
    el.classList.toggle("flex", enabled)
  }

  updatePreview() {
    const points = this.readInt(
      this.pointsEarnedTarget
    )
    const unitAmount = this.readCurrency(
      this.currencyUnitTarget
    )
    const pointValue = this.readCurrency(
      this.pointValueTarget
    )
    const minimum = this.readInt(
      this.minimumPointsTarget
    )

    if (
      !this.isPositive(points) ||
      !this.isPositive(unitAmount) ||
      !this.isPositive(pointValue) ||
      !this.isPositive(minimum)
    ) {
      this.previewSpendTarget.textContent =
        this.emptyMessageValue
      this.previewRedeemTarget.textContent = ""
      this.previewRatioTarget.textContent = ""
      this.previewMinimumTarget.textContent = ""
      return
    }

    const sample = this.sampleSpendValue
    const earnedPoints = Math.floor(
      (sample / unitAmount) * points
    )
    const discountValue = earnedPoints * pointValue
    const ratio =
      (pointValue * points) / unitAmount
    const percent = ratio * 100
    const label = this.ratioLabel(ratio)
    const minSpend =
      (minimum * unitAmount) / points

    this.previewSpendTarget.textContent =
      this.fillTemplate(this.spendTemplateValue, {
        spend: this.formatCurrency(sample),
        points: this.formatInt(earnedPoints),
      })

    this.previewRedeemTarget.textContent =
      this.fillTemplate(
        this.redeemTemplateValue,
        {
          discount:
            this.formatCurrency(discountValue),
        }
      )

    this.previewRatioTarget.textContent =
      this.fillTemplate(this.ratioTemplateValue, {
        percent: percent.toFixed(1),
        label,
      })

    this.previewMinimumTarget.textContent =
      this.fillTemplate(
        this.minimumTemplateValue,
        {
          minimum: this.formatInt(minimum),
          minSpend: this.formatCurrency(minSpend),
        }
      )
  }

  ratioLabel(ratio) {
    if (ratio >= 0.5) {
      return this.labelGenerousValue
    }
    if (ratio >= 0.05) {
      return this.labelBalancedValue
    }
    return this.labelConservativeValue
  }

  fillTemplate(template, params) {
    if (!template) return ""
    return template.replace(
      /%\{(\w+)\}/g,
      (_match, key) => {
        const value = params[key]
        return value === undefined ? "" : value
      }
    )
  }

  setIntegerValue(input, value) {
    input.value = String(value)
    this.dispatchInput(input)
  }

  setMaskedCurrencyValue(input, value) {
    // `currency-input` wraps the input with
    // IMask. Route the update through the mask
    // so the display formats correctly; fall
    // back to the raw value if the mask hasn't
    // initialized yet.
    const mask = this.maskFor(input)
    if (mask) {
      mask.typedValue = value
    } else {
      input.value = String(value)
    }
    this.dispatchInput(input)
  }

  maskFor(input) {
    const wrapper = input.closest(
      "[data-controller~='currency-input']"
    )
    if (!wrapper) return null
    const controller =
      this.application
        .getControllerForElementAndIdentifier(
          wrapper,
          "currency-input"
        )
    return controller?.mask || null
  }

  dispatchInput(input) {
    input.dispatchEvent(
      new Event("input", { bubbles: true })
    )
  }

  readInt(input) {
    const raw = parseInt(input.value, 10)
    return Number.isFinite(raw) ? raw : 0
  }

  readCurrency(input) {
    const mask = this.maskFor(input)
    if (mask) {
      const typed = parseFloat(mask.typedValue)
      return Number.isFinite(typed) ? typed : 0
    }
    const cleaned = input.value.replace(
      /[^0-9.\-]/g, ""
    )
    const parsed = parseFloat(cleaned)
    return Number.isFinite(parsed) ? parsed : 0
  }

  isPositive(value) {
    return Number.isFinite(value) && value > 0
  }

  formatCurrency(value) {
    return this.formatter.format(value)
  }

  formatInt(value) {
    return this.integerFormatter.format(value)
  }
}
